import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '../ui/command';
import { Check, ChevronsUpDown, Eraser, Loader2, Plus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import AltaProfesionalDialog from './AltaProfesionalDialog';
import { esTipoConAlta, esTipoMedico, etiquetaColegiado } from '../../lib/medicos';

/**
 * CatalogCombobox — Phase 27.7
 *
 * Desplegable con búsqueda para los campos del Registro Quirúrgico que se
 * alimentan del catálogo compartido (GET /api/qx-catalogo?tipo=…). Si el
 * nombre que busca el usuario no existe, la última fila ofrece
 * "Añadir «lo tecleado»": hace POST /api/qx-catalogo (idempotente por
 * tipo+nombre) para que quede guardado para la próxima vez, y lo selecciona.
 *
 * Props:
 *   - tipo: 'cirujano' | 'anestesista' | 'instrumentista' | 'circulante'
 *           | 'clinica_origen' | 'lugar'
 *   - value: string actual (texto libre; puede no estar en el catálogo)
 *   - onChange(nombre, entry|null): entry es la fila completa del catálogo
 *     ({id, tipo, nombre, clinica}) para permitir autorrellenos en el padre.
 *   - placeholder: texto cuando no hay valor
 *   - extraOnSelect?(entry): callback adicional tras seleccionar/añadir
 *   - clinicaParaNuevo?: clínica que se adjunta al POST al añadir a mano
 *   - allowEmpty?: muestra la opción "Vaciar campo" cuando hay valor
 *   - allowAdd?: (true) ofrece "Añadir «lo tecleado»" al catálogo. Con
 *     false el campo es SOLO desplegable: se busca pero no se inventa nada
 *     (el cirujano del parte, dirección 2026-09-08: con texto libre se escribía
 *     la clínica en vez del cirujano y no se podía facturar).
 *     En cirujanos y anestesistas (dirección, 2026-09-08, punto 7) nunca hay
 *     texto libre: la fila de añadir abre el ALTA con formato obligatorio
 *     «Dr./Dra. Nombre Apellido1 Apellido2» + nº de colegiado
 *     (AltaProfesionalDialog); el nuevo queda en el desplegable al momento.
 *     Cada fila enseña el colegiado («Col. 281234567» o «Col. pendiente»).
 *     En instrumentistas y circulantes (administración, 2026-09-09, punto 7) pasa
 *     lo mismo, con «Nombre Apellido1 Apellido2» y sin colegiado: no son
 *     médicos. Solo clínicas y lugares admiten texto libre.
 *   - dataTestid, disabled
 *
 * Funciona dentro de un Dialog (mismo patrón Popover+Command que
 * PatientCombobox, ya usado en CitaCreateDialog / SesionDialog) y con
 * teclado: flechas para moverse, Enter selecciona la fila resaltada
 * (o la de "Añadir" si no hay coincidencias).
 */

// Comparación sin tildes ni mayúsculas (mismo criterio que PatientCombobox).
const _norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export function CatalogCombobox({
  tipo,
  value,
  onChange,
  placeholder = 'Selecciona…',
  extraOnSelect,
  clinicaParaNuevo,
  allowEmpty = false,
  allowAdd = true,
  dataTestid,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [altaOpen, setAltaOpen] = useState(false);
  const esMedico = esTipoMedico(tipo);
  // Los tipos con ALTA con formato: médicos (Dr./Dra. + colegiado) y personal
  // de quirófano (instrumentistas y circulantes: nombre y dos apellidos,
  // Administración 2026-09-09, punto 7). En ellos no hay texto libre.
  const conAlta = esTipoConAlta(tipo);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/qx-catalogo', { params: { tipo } });
      setEntries(Array.isArray(r.data) ? r.data : []);
    } catch {
      // Silencioso: el usuario aún puede teclear y usar "Añadir".
    } finally {
      setLoading(false);
    }
  }, [tipo]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Al abrir, refrescamos en segundo plano (por si otro usuario añadió
  // nombres) y limpiamos la búsqueda anterior.
  const handleOpenChange = (o) => {
    setOpen(o);
    if (o) {
      setSearch('');
      fetchEntries();
    }
  };

  const trimmed = search.trim();

  // Filtramos nosotros (shouldFilter=false) para que la fila "Añadir" nunca
  // desaparezca por el filtro interno de cmdk.
  const filtered = useMemo(() => {
    const q = _norm(trimmed);
    if (!q) return entries;
    return entries.filter((e) => _norm(e.nombre).includes(q));
  }, [entries, trimmed]);

  const exactExists = useMemo(
    () => entries.some((e) => _norm(e.nombre) === _norm(trimmed)),
    [entries, trimmed]
  );
  // Texto libre: solo en los tipos que no son personas (clínicas y lugares).
  const canAdd = allowAdd && !conAlta && trimmed.length > 0 && !exactExists;
  // Médicos y personal: la fila de alta está siempre (con o sin texto tecleado).
  const canAlta = allowAdd && conAlta && !exactExists;

  const finishSelect = (nombre, entry) => {
    onChange && onChange(nombre, entry || null);
    if (entry && extraOnSelect) extraOnSelect(entry);
    setOpen(false);
  };

  const handleAlta = () => {
    setOpen(false);
    setAltaOpen(true);
  };

  const onAltaCreated = (entry) => {
    setEntries((prev) => {
      const rest = prev.filter((e) => e.id !== entry.id);
      return [...rest, entry].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
    });
    finishSelect(entry.nombre, entry);
  };

  const handleAdd = async () => {
    if (conAlta) {
      handleAlta();
      return;
    }
    if (!trimmed || adding) return;
    setAdding(true);
    try {
      const payload = { tipo, nombre: trimmed };
      if (clinicaParaNuevo) payload.clinica = clinicaParaNuevo;
      const r = await api.post('/api/qx-catalogo', payload);
      const entry = r.data;
      // Insertar/actualizar en la lista local manteniendo el orden alfabético.
      setEntries((prev) => {
        const rest = prev.filter((e) => e.id !== entry.id);
        return [...rest, entry].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
      });
      toast.success('Añadido al catálogo');
      finishSelect(entry.nombre, entry);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo añadir al catálogo');
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
    {conAlta && (
      <AltaProfesionalDialog
        open={altaOpen}
        onOpenChange={setAltaOpen}
        tipo={tipo}
        textoInicial={trimmed}
        clinica={clinicaParaNuevo}
        onCreated={onAltaCreated}
      />
    )}
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={dataTestid}
          className="w-full justify-between font-normal"
        >
          <span className={`truncate ${!value ? 'text-muted-foreground' : ''}`}>{value || placeholder}</span>
          {loading && entries.length === 0
            ? <Loader2 className="w-4 h-4 ml-2 opacity-60 animate-spin shrink-0" />
            : <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={allowAdd && !conAlta ? 'Buscar o escribir nuevo…' : 'Buscar en la lista…'}
            value={search}
            onValueChange={setSearch}
            data-testid={dataTestid ? `${dataTestid}-input` : undefined}
          />
          <CommandList>
            {loading && entries.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando catálogo…
              </div>
            )}

            {allowEmpty && value && !trimmed && (
              <CommandGroup>
                <CommandItem
                  value="__empty__"
                  onSelect={() => finishSelect('', null)}
                  className="text-muted-foreground"
                  data-testid={dataTestid ? `${dataTestid}-clear` : undefined}
                >
                  <Eraser className="w-4 h-4" /> Vaciar campo
                </CommandItem>
              </CommandGroup>
            )}

            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((e) => (
                  <CommandItem
                    key={e.id}
                    value={e.nombre}
                    onSelect={() => finishSelect(e.nombre, e)}
                    data-testid={dataTestid ? `${dataTestid}-item` : undefined}
                  >
                    <Check className={`h-4 w-4 ${value === e.nombre ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="truncate">{e.nombre}</span>
                    {(esMedico || e.clinica) && (
                      <span className="ml-auto pl-2 text-xs text-muted-foreground shrink-0">
                        {esMedico ? etiquetaColegiado(e.colegiado) : ''}
                        {esMedico && e.clinica ? ' · ' : ''}
                        {e.clinica || ''}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {!loading && filtered.length === 0 && !canAdd && (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground" data-testid={dataTestid ? `${dataTestid}-vacio` : undefined}>
                {entries.length === 0
                  ? (allowAdd ? (conAlta ? 'Lista vacía. Da de alta al primero aquí abajo.' : 'Catálogo vacío. Escribe un nombre para añadirlo.') : 'Catálogo vacío: se da de alta en el Catálogo QX.')
                  : (allowAdd ? (conAlta ? 'Sin coincidencias: elige de la lista o da de alta al profesional.' : 'Sin coincidencias.') : 'Sin coincidencias: solo se puede elegir de la lista.')}
              </div>
            )}

            {canAlta && (
              <>
                {filtered.length > 0 && <CommandSeparator />}
                <CommandGroup>
                  <CommandItem
                    value={`__alta__:${trimmed}`}
                    onSelect={handleAlta}
                    className="text-[#13231E] font-medium"
                    data-testid="catalog-alta"
                  >
                    <UserPlus className="w-4 h-4" />
                    {trimmed
                      ? `Dar de alta a «${trimmed}» (${esMedico ? 'Dr./Dra. Nombre y dos apellidos + colegiado' : 'Nombre y dos apellidos'})`
                      : `Dar de alta un ${tipo} nuevo…`}
                  </CommandItem>
                </CommandGroup>
              </>
            )}

            {canAdd && (
              <>
                {filtered.length > 0 && <CommandSeparator />}
                <CommandGroup>
                  <CommandItem
                    value={`__add__:${trimmed}`}
                    onSelect={handleAdd}
                    disabled={adding}
                    className="text-[#13231E] font-medium"
                    data-testid="catalog-add"
                  >
                    {adding
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Plus className="w-4 h-4" />}
                    Añadir «{trimmed}»
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    </>
  );
}

export default CatalogCombobox;
